import React, { useState } from "react";
import { Button, TrashIcon } from "../../../components/ui";
import {
  lockedProps,
  type GateProps,
} from "../../billing/hooks/useSubscriptionGate";

export interface OptionActionsProps {
  /** What the option offers beside its delete — Modify when read, Save when written. */
  children: React.ReactNode;
  /** Zero-based, for naming the delete after the option it belongs to. */
  index: number;
  onRemove: () => void;
  /**
   * Set when there is nothing for the delete to do — the meal's only option,
   * with nothing typed into it. Taking it out would only put an identical one
   * back, so the bin greys out rather than asking a question with one answer.
   */
  removeDisabled?: boolean;
  /**
   * Whether the workspace may write this plan at all — see
   * `useSubscriptionGate`. The bin needs its own answer rather than relying on
   * Save beside it: deleting an option the backend holds goes straight out as
   * it is pressed, so it is not one of the writes that Save is the gate on.
   */
  gate?: GateProps;
}

/**
 * The pair of actions at the foot of an option: whatever it does, and deleting
 * it.
 *
 * Deleting asks where the icon stood rather than opening a dialog — the answer
 * belongs beside the thing it is about, and the option stays on screen to be
 * looked at while it is answered. The other action stands down while the
 * question is up, so there is one thing to answer.
 *
 * Shared by both halves of the option so the question reads the same however
 * the option was being looked at when it was asked.
 */
const OptionActions: React.FC<OptionActionsProps> = ({
  children,
  index,
  onRemove,
  removeDisabled = false,
  gate = { disabled: false },
}) => {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex animate-confirm-in items-center gap-3">
        <Button
          variant="danger"
          size="sm"
          onClick={onRemove}
          className="whitespace-nowrap"
        >
          Confirm delete
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setConfirming(false)}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {children}
      <button
        type="button"
        aria-label={`Delete option ${index + 1}`}
        // A bin with nothing to delete stays plainly disabled — there is
        // nothing to explain. A workspace with no plan behind it has something
        // to do about it, so that press is turned back to the reason rather
        // than swallowed. See `lockedProps`.
        {...(removeDisabled
          ? { disabled: true, title: "Nothing to delete" }
          : lockedProps(gate, () => setConfirming(true)))}
        // Red while it will delete something, grey while it will not — the
        // bin's two states, straight off the design.
        className={`rounded-md p-2 transition-colors ${
          removeDisabled || gate.disabled
            ? "cursor-not-allowed text-[#8B8D98]"
            : "text-[#FF3B30] hover:bg-[#FDECEC]"
        }`}
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

export default OptionActions;
